/**
 * Replicates the threshold/formatting helpers from @chatwoot/utils/src/sla.ts
 * so that we can build a multi-timer list without importing unexported symbols.
 */

const calculateThreshold = (timeOffset, threshold) => {
  if (threshold == null) return null;
  const currentTime = Math.floor(Date.now() / 1000);
  return timeOffset + threshold - currentTime;
};

const formatSLATime = seconds => {
  const units = {
    y: 31536000,
    mo: 2592000,
    d: 86400,
    h: 3600,
    m: 60,
  };

  if (seconds < 60) return '1m';

  const parts = [];
  let remaining = seconds;
  Object.keys(units).forEach(unit => {
    if (parts.length === 2) return;
    const value = Math.floor(remaining / units[unit]);
    if (value > 0) {
      parts.push(value + unit);
      remaining -= value * units[unit];
    }
  });
  return parts.join(' ');
};

const makeSLAEntry = (type, timeOffset, threshold) => {
  const t = calculateThreshold(timeOffset, threshold);
  if (t === null) return null;
  return {
    type,
    threshold: formatSLATime(t <= 0 ? -t : t),
    icon: t <= 0 ? 'flame' : 'alarm',
    isSlaMissed: t <= 0,
  };
};

/**
 * Returns the list of SLA status entries to display simultaneously.
 *
 * Display rules:
 *  - Before first reply:
 *      • Show FRT if configured.
 *      • If FRT is not configured but RT is, show RT from the start.
 *  - After first reply:
 *      • Show NRT if configured and waiting_since is set.
 *      • Show RT if configured and conversation is open.
 *      • Both may show at the same time.
 *
 * @param {Object} appliedSla - Applied SLA in snake_case (after useSLADeadline adjustment)
 * @param {Object} chat       - Conversation object (snake_case fields)
 * @returns {Array<{type, threshold, icon, isSlaMissed}>}
 */
export function useSLAStatuses(appliedSla, chat) {
  if (!appliedSla || !chat) return [];

  const {
    sla_first_response_time_threshold: frtThreshold,
    sla_next_response_time_threshold: nrtThreshold,
    sla_resolution_time_threshold: rtThreshold,
    created_at: createdAt,
  } = appliedSla;

  const {
    first_reply_created_at: firstReplyCreatedAt,
    waiting_since: waitingSince,
    status,
  } = chat;

  const hasFRT = frtThreshold != null;
  const hasNRT = nrtThreshold != null;
  const hasRT = rtThreshold != null;
  const hasFirstReply = !!firstReplyCreatedAt && firstReplyCreatedAt !== 0;

  if (!hasFirstReply) {
    // Phase 1 — waiting for first agent reply
    if (hasFRT) {
      const entry = makeSLAEntry('FRT', createdAt, frtThreshold);
      return entry ? [entry] : [];
    }
    // No FRT configured → RT starts counting from conversation creation
    if (hasRT && status === 'open') {
      const entry = makeSLAEntry('RT', createdAt, rtThreshold);
      return entry ? [entry] : [];
    }
    return [];
  }

  // Phase 2 — first reply has been sent, show NRT and/or RT
  const results = [];
  if (hasNRT && waitingSince) {
    const entry = makeSLAEntry('NRT', waitingSince, nrtThreshold);
    if (entry) results.push(entry);
  }
  if (hasRT && status === 'open') {
    const entry = makeSLAEntry('RT', createdAt, rtThreshold);
    if (entry) results.push(entry);
  }
  return results;
}
